// src/pages/appointment-calendar/index.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumbs from "../../components/ui/Breadcrumbs";
// import QuickActionButton from "../../components/ui/QuickActionButton";
import CalendarHeader from "./components/CalendarHeader";
import CalendarView from "./components/CalendarView";
import MobileCalendarView from "./components/MobileCalendarView";
import AppointmentSidebar from "./components/AppointmentSidebar";
import AppointmentModal from "./components/AppointmentModal";
import WorkingHoursControl from "./components/WorkingHoursControl";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";

import api from "../../lib/api";
import { listTimeOff } from "../../lib/timeOff";
import { useAuth } from "../../auth/AuthProvider";

const AppointmentCalendar = () => {
  const { t, i18n } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState("week");
  const [selectedStaff, setSelectedStaff] = useState("all");

  // staff list (din DB) - folosit pentru multi-select pe mobil
  const [staffOptions, setStaffOptions] = useState([]);

  // Mobile: multi-select staff ("All" = listă goală)
  const [mobileStaffIds, setMobileStaffIds] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const bid = window.localStorage.getItem("current_business_id") || "";
      const key = bid ? `calendar_mobile_staff_ids_${bid}` : "calendar_mobile_staff_ids";
      const raw = window.localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  });

  const [showAppointmentSidebar, setShowAppointmentSidebar] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [modalMode, setModalMode] = useState("view");

  const [pendingOpenAppointmentId, setPendingOpenAppointmentId] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [workingHours, setWorkingHours] = useState(null);
  const [timeOffPeriods, setTimeOffPeriods] = useState([]);

  // interval dintre programări (slot de grilă)
  const [slotMinutes, setSlotMinutes] = useState(15);

  // Mobile: Date picker modal (calendar grid)
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dpMonth, setDpMonth] = useState(() => new Date());   // luna afișată în calendar
  const [dpSelected, setDpSelected] = useState(() => new Date()); // ziua selectată


  const location = useLocation();
  const navigate = useNavigate();
  const { permissions } = useAuth();

  // Owner poate vedea pe toate sucursalele sau pe una (scope setat din Header)
  const isOwnerLike =
    permissions?.isSuperAdmin || permissions?.isOwnerCurrentBusiness;

  const [locationScopeId, setLocationScopeId] = useState(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem("current_location_scope_id") || "";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const sync = () => {
      if (!isOwnerLike) {
        setLocationScopeId("");
        return;
      }
      setLocationScopeId(
        window.localStorage.getItem("current_location_scope_id") || ""
      );
    };

    sync();
    window.addEventListener("location-scope:changed", sync);
    return () => window.removeEventListener("location-scope:changed", sync);
  }, [isOwnerLike]);

  /* ---------- Helpers intervale după view ---------- */
  const startOfWeek = (d) => {
    const x = new Date(d);
    const wd = (x.getDay() + 6) % 7; // luni = 0
    x.setHours(0, 0, 0, 0);
    x.setDate(x.getDate() - wd);
    return x;
  };

  const endOfWeek = (d) => {
    const x = startOfWeek(d);
    x.setDate(x.getDate() + 7);
    return x;
  };

  const startOfMonth = (d) => {
    const x = new Date(d.getFullYear(), d.getMonth(), 1);
    x.setHours(0, 0, 0, 0);
    return x;
  };

  const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 1);

  // formatăm data în local time, fără toISOString (evităm -1 zi)
  const formatDateLocal = (d) => {
    if (!d) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const isSameDay = (a, b) => {
    if (!a || !b) return false;
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  };

  const monthLabel = (d) => {
    if (!d) return "";
    // ex: "ianuarie 2026"
    return d.toLocaleDateString(i18n.language || "ro", { month: "long", year: "numeric" });
  };

  // luni = 0 ... duminică = 6
  const mondayIndex = (date) => (date.getDay() + 6) % 7;

  const buildMonthGrid = (monthDate) => {
    const y = monthDate.getFullYear();
    const m = monthDate.getMonth();
    const first = new Date(y, m, 1);

    // începem grila cu luni
    const start = new Date(first);
    start.setDate(first.getDate() - mondayIndex(first));

    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);

      cells.push({
        date: d,
        inMonth: d.getMonth() === m,
      });
    }
    return cells;
  };

  const weekDays = ["L", "Ma", "Mi", "J", "V", "S", "D"];

  /* ---------- Deschidere Sidebar pe ?action=create ---------- */
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (q.get("action") === "create") {
      setShowAppointmentSidebar(true);
      q.delete("action");
      navigate({ pathname: location.pathname, search: q.toString() ? `?${q.toString()}` : "" }, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  /* ---------- Filtru personal din dashboard (?staffId) ---------- */
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const staffId = q.get("staffId");
    if (staffId) {
      setSelectedStaff(staffId);
      q.delete("staffId");
      navigate({ pathname: location.pathname, search: q.toString() ? `?${q.toString()}` : "" }, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);


  /* ---------- Deschidere programare din notificări (?appointment_id / ?appt) ---------- */
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const rawId = q.get("appt") || q.get("appointment_id") || q.get("appointmentId");
    if (!rawId) return;

    const apptId = parseInt(String(rawId), 10);
    if (!apptId) return;

    // Dacă vine și start/at, mutăm calendarul pe ziua aia (ca să intre în intervalul de fetch)
    const rawStart = q.get("start") || q.get("at") || q.get("start_at");
    if (rawStart) {
      const d = new Date(rawStart);
      if (!Number.isNaN(d.getTime())) {
        setCurrentDate(d);
      }
    }

    setPendingOpenAppointmentId(apptId);

    // Forțează un refetch (dacă ești deja pe calendar și tocmai ai venit din Bell)
    try {
      window.dispatchEvent(new CustomEvent("appointments:refetch"));
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (!pendingOpenAppointmentId) return;

    const found = (appointments || []).find(
      (a) => Number(a?.id) === Number(pendingOpenAppointmentId)
    );
    if (!found) return;

    setSelectedAppointment(found);
    setModalMode("view");
    setShowAppointmentModal(true);
    setPendingOpenAppointmentId(null);

    // Curățăm parametrii din URL ca să nu se redeschidă la refresh/back
    try {
      const q = new URLSearchParams(location.search);
      ["appt", "appointment_id", "appointmentId", "start", "at", "start_at"].forEach((k) => q.delete(k));
      navigate({ pathname: location.pathname, search: q.toString() ? `?${q.toString()}` : "" }, { replace: true });
    } catch {
      // ignore
    }
  }, [pendingOpenAppointmentId, appointments, location.search, location.pathname, navigate]);

  /* ---------- Citire interval din localStorage ---------- */
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem("calendar_slot_minutes");
      const v = raw ? parseInt(raw, 10) : NaN;
      if (v && v > 0) {
        setSlotMinutes(v);
      }
    } catch {
      // ignore
    }
  }, []);

  const getBusinessId = useCallback(() => {
    try {
      return window.localStorage.getItem("current_business_id") || null;
    } catch {
      return null;
    }
  }, []);

  /* ---------- Fetch staff (pentru multi-select pe mobil) ---------- */
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const bid = getBusinessId();
        if (!bid) {
          if (!cancelled) setStaffOptions([]);
          return;
        }
        const { data } = await api.get("/b/staff", { headers: { "X-Business-Id": bid } });
        if (cancelled) return;
        if (!Array.isArray(data)) {
          setStaffOptions([]);
          return;
        }
        setStaffOptions(
          data.map((u) => ({
            id: u.id,
            label:
              (u.first_name || u.last_name
                ? `${u.first_name || ""} ${u.last_name || ""}`.trim()
                : u.label || u.username || `#${u.id}`),
          }))
        );
      } catch (e) {
        console.error("Eroare la încărcarea staff-ului:", e?.response?.data || e);
        if (!cancelled) setStaffOptions([]);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [getBusinessId]);

  /* ---------- Persist mobile staff selection ---------- */
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const bid = window.localStorage.getItem("current_business_id") || "";
      const key = bid ? `calendar_mobile_staff_ids_${bid}` : "calendar_mobile_staff_ids";
      const safe = Array.isArray(mobileStaffIds) ? mobileStaffIds.slice(0, 12).map(String) : [];
      window.localStorage.setItem(key, JSON.stringify(safe));
    } catch {
      // ignore
    }
  }, [mobileStaffIds]);

  /* ---------- Fetch programări (inițial + refetch custom) ---------- */
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    const load = async () => {
      try {
        let from, to;
        if (calendarView === "day") {
          from = new Date(currentDate);
          from.setHours(0, 0, 0, 0);
          to = new Date(from.getTime() + 24 * 60 * 60 * 1000);
        } else if (calendarView === "week") {
          from = startOfWeek(currentDate);
          to = endOfWeek(currentDate);
        } else {
          // 'month'
          from = startOfMonth(currentDate);
          to = endOfMonth(currentDate);
        }

        const bid = getBusinessId();

        if (!bid) {
          console.warn("Nu există current_business_id în localStorage – nu pot încărca programările.");
          if (alive) setAppointments([]);
          return;
        }

        const params = { from: from.toISOString(), to: to.toISOString() };
        if (isOwnerLike && locationScopeId) {
          params.location_id = locationScopeId;
        }

        const { data } = await api.get("/b/appointments", {
          params,
          signal: controller.signal,
          headers: { "X-Business-Id": bid },
        });

        if (!alive) return;

        const raw = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
            ? data.results
            : Array.isArray(data?.items)
              ? data.items
              : [];

        const events = raw.map((it) => {
          const serviceIds = Array.isArray(it.service_ids)
            ? it.service_ids
            : Array.isArray(it.serviceIds)
              ? it.serviceIds
              : [];

          const serviceNames = Array.isArray(it.service_names)
            ? it.service_names
            : Array.isArray(it.serviceNames)
              ? it.serviceNames
              : [];

          const primaryServiceId =
            it.service_id ??
            (typeof it.service === "number" ? it.service : it.service?.id) ??
            (serviceIds.length ? serviceIds[0] : null);

          const primaryServiceName = it.service_name || it.service?.name || "";
          const combinedServiceName =
            serviceNames.length > 0
              ? serviceNames.filter(Boolean).join(", ")
              : primaryServiceName;

          return {
            id: it.id,
            start: new Date(it.start_at),
            end: new Date(it.end_at || it.start_at),
            title: `${it.client_name || ""}${it.client_name ? " · " : ""}${combinedServiceName || ""}`,

            clientName: it.client_name || "",
            serviceName: combinedServiceName,
            serviceId: primaryServiceId,
            serviceIds: serviceIds.length ? serviceIds : primaryServiceId ? [primaryServiceId] : [],
            serviceNames: serviceNames.length ? serviceNames : primaryServiceName ? [primaryServiceName] : [],
            service: { id: primaryServiceId, name: primaryServiceName || combinedServiceName },
            // folosit pentru filtrare / grid multi-staff
            staffId: String(
              it.staff_id ??
              it.staff_user_id ??
              (typeof it.staff === "number" ? it.staff : it.staff?.id) ??
              ""
            ),
            staffName: it.staff_name || "",
            status: it.status || "booked",

            _raw: it,
          };
        });

        setAppointments(events);
      } catch (err) {
        const canceled = err?.name === "CanceledError" || err?.code === "ERR_CANCELED";
        if (!canceled) console.error("Eșec încărcare programări:", err?.response?.data || err);
      }
    };

    load();

    const refetch = () => load();
    window.addEventListener("appointments:refetch", refetch);
    window.addEventListener("location-scope:changed", refetch);

    return () => {
      alive = false;
      controller.abort();
      window.removeEventListener("appointments:refetch", refetch);
      window.removeEventListener("location-scope:changed", refetch);
    };
  }, [currentDate, calendarView, getBusinessId, locationScopeId, isOwnerLike]);

  /* ---------- Fetch time-off pentru perioada vizibilă ---------- */
  useEffect(() => {
    let cancelled = false;
    const bid = getBusinessId();
    if (!bid) { setTimeOffPeriods([]); return; }

    const load = async () => {
      try {
        let from, to;
        if (calendarView === "day") {
          from = new Date(currentDate); from.setHours(0, 0, 0, 0);
          to = new Date(from.getTime() + 24 * 60 * 60 * 1000);
        } else if (calendarView === "week") {
          from = startOfWeek(currentDate);
          to = endOfWeek(currentDate);
        } else {
          from = startOfMonth(currentDate);
          to = endOfMonth(currentDate);
        }
        const list = await listTimeOff({
          status: "approved",
          from: from.toISOString(),
          to: to.toISOString(),
        });
        if (cancelled) return;
        setTimeOffPeriods(
          (Array.isArray(list) ? list : []).map((r) => ({
            staffId: String(r.staff_id || r.staff_user || ""),
            staffName: r.staff_name || "",
            start: new Date(r.start_at || r.start_date),
            end: new Date(r.end_at || r.end_date),
          }))
        );
      } catch {
        if (!cancelled) setTimeOffPeriods([]);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [currentDate, calendarView, getBusinessId]);

  /* ---------- Fetch program de lucru pentru locația curentă ---------- */
  useEffect(() => {
    if (typeof window === "undefined") return;

    let cancelled = false;
    let lastLocId = null;

    const getLocId = () => {
      const raw = window.localStorage.getItem("current_location_id");
      return raw ? Number(raw) || null : null;
    };

    const loadWorkingHours = async (locId) => {
      const bid = getBusinessId();

      if (!locId || !bid) {
        if (!cancelled) setWorkingHours(null);
        return;
      }

      try {
        const { data } = await api.get("/b/working-hours", {
          params: { location_id: locId },
          headers: { "X-Business-Id": bid },
        });
        if (!cancelled) {
          setWorkingHours(data);
        }
      } catch (e) {
        console.error("Eroare la încărcarea programului de lucru:", e?.response?.data || e);
        if (!cancelled) setWorkingHours(null);
      }
    };

    const checkLocation = () => {
      const locId = getLocId();

      if (!locId) {
        if (!cancelled && lastLocId !== null) {
          setWorkingHours(null);
        }
        lastLocId = null;
        return;
      }

      if (locId === lastLocId) return;
      lastLocId = locId;
      loadWorkingHours(locId);
    };

    checkLocation();
    const intervalId = setInterval(checkLocation, 1000);

    const onWorkingHoursUpdated = (e) => {
      const locIdFromEvent = e?.detail?.locationId;
      const locId = locIdFromEvent ?? getLocId();
      if (!locId) return;
      loadWorkingHours(locId);
    };

    window.addEventListener("working-hours:updated", onWorkingHoursUpdated);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      window.removeEventListener("working-hours:updated", onWorkingHoursUpdated);
    };
  }, [getBusinessId]);

  /* ---------- Breadcrumbs ---------- */
  const canSeeDashboard = Boolean(permissions?.canSeeDashboard);

  const breadcrumbItems = canSeeDashboard
    ? [
      { label: "Dashboard", path: "/app/admin-dashboard" },
      { label: t("calendar.breadcrumb"), path: "/app/appointment-calendar" },
    ]
    : [{ label: t("calendar.breadcrumb"), path: "/app/appointment-calendar" }];

  /* ---------- Handlers UI ---------- */
  const handleToggleSidebar = () => setSidebarCollapsed((v) => !v);

  const handleViewChange = (view) => setCalendarView(view);

  const handleNavigate = (direction) => {
    const newDate = new Date(currentDate);
    if (calendarView === "day") {
      newDate.setDate(newDate.getDate() + (direction === "next" ? 1 : -1));
    } else if (calendarView === "week") {
      newDate.setDate(newDate.getDate() + (direction === "next" ? 7 : -7));
    } else if (calendarView === "month") {
      newDate.setMonth(newDate.getMonth() + (direction === "next" ? 1 : -1));
    }
    setCurrentDate(newDate);
  };

  const handleTodayClick = () => setCurrentDate(new Date());

  const openDatePicker = () => {
    const cur = new Date(currentDate);
    setDpSelected(cur);
    setDpMonth(new Date(cur.getFullYear(), cur.getMonth(), 1));
    setShowDatePicker(true);
  };


  const applyDatePicker = () => {
    if (!dpSelected) return;

    setCurrentDate(new Date(dpSelected));

    // dacă ești în month view, când alegi o zi -> treci în day
    if (calendarView === "month") setCalendarView("day");

    setShowDatePicker(false);
  };


  const handleCreateAppointment = (appointmentData = null) => {
    if (appointmentData) {
      const normalized = { ...appointmentData };

      // compat: unele locuri trimit `staff`, dar AppointmentModal citește `staffId`
      if (normalized.staff && !normalized.staffId) normalized.staffId = normalized.staff;

      setSelectedAppointment(normalized);
    } else {
      setSelectedAppointment(null);
    }

    setModalMode("create");
    setShowAppointmentModal(true);
    setShowAppointmentSidebar(false);
  };

  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    setModalMode("view");
    setShowAppointmentModal(true);
  };

  const handleDateSelect = (date, time = null, staffIdFromColumn = null) => {
    if (time) {
      const staffId =
        staffIdFromColumn ??
        (selectedStaff !== "all" ? selectedStaff : "");

      const appointmentData = {
        date: formatDateLocal(date),
        time,
        staffId, // <-- AppointmentModal citește staffId
      };

      setSelectedAppointment(appointmentData);
      setModalMode("create");
      setShowAppointmentModal(true);
      return;
    }

    setCurrentDate(date);
    if (calendarView === "month") setCalendarView("day");
  };




  const handleStaffChange = (staffId) => setSelectedStaff(staffId);

  const handleSlotChange = (e) => {
    const v = Number(e.target.value) || 15;
    setSlotMinutes(v);
    try {
      window.localStorage.setItem("calendar_slot_minutes", String(v));
    } catch {
      // ignore
    }
  };

  /* ---------- Salvare / ștergere programare (Modal) ---------- */
  const handleAppointmentSave = async (payload) => {
    // ✅ IMPORTANT: nu mai închidem modalul în finally.
    // AppointmentModal afișează deja erorile; aici propagăm error-ul către el.
    const bid = getBusinessId();
    if (!bid) {
      const err = new Error("Nu există current_business_id în localStorage. Nu pot salva programarea.");
      console.error(err.message);
      throw err;
    }

    // acceptăm atât serviceId (legacy) cât și serviceIds (multi-servicii)
    const rawServiceIds = Array.isArray(payload?.serviceIds)
      ? payload.serviceIds
      : payload?.serviceId
      ? [payload.serviceId]
      : [];
    const serviceIds = rawServiceIds
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n));
    const primaryServiceId = serviceIds?.[0] || (payload?.serviceId ? Number(payload.serviceId) : null);

    const startISO =
      payload.startISO ||
      (payload.date && payload.time ? new Date(`${payload.date}T${payload.time}`).toISOString() : null);

    let endISO = null;
    if (startISO) {
      const end = new Date(startISO);
      if (payload.duration) {
        end.setMinutes(end.getMinutes() + Number(payload.duration || 0));
      }
      endISO = end.toISOString();
    }

    const appointmentId = payload.id || (selectedAppointment && selectedAppointment.id) || null;

    const clientId = payload?.clientId ? Number(payload.clientId) : null;

    const body = {
      start: startISO || undefined,
      end: endISO || undefined,
      status: payload.status ?? "booked",
      notes: payload.notes || undefined,
      service: primaryServiceId || undefined,
      services: serviceIds.length ? serviceIds : undefined,
      staff: payload.staffId || undefined,
      client_id: clientId || undefined,
      client_name: !clientId ? payload.clientName?.trim() || undefined : undefined,
      client_phone: !clientId ? payload.clientPhone?.trim() || undefined : undefined,
      location_id: payload.locationId || undefined,
    };

    if (appointmentId) {
      await api.patch(`/b/appointments/${appointmentId}`, body, {
        headers: { "X-Business-Id": bid },
      });
    } else {
      await api.post(`/b/appointments`, body, {
        headers: { "X-Business-Id": bid },
      });
    }

    // ✅ doar pe succes:
    setShowAppointmentModal(false);
    window.dispatchEvent(new CustomEvent("appointments:refetch"));
  };

  const handleAppointmentDelete = async (appointmentId) => {
    setShowAppointmentModal(false);
    try {
      const bid = getBusinessId();
      if (!bid) {
        console.error("Nu există current_business_id în localStorage. Nu pot șterge programarea.");
        return;
      }
      await api.delete(`/b/appointments/${appointmentId}`, {
        headers: { "X-Business-Id": bid },
      });
    } catch (e) {
      console.error("Eroare la ștergere programare:", e?.response?.data || e.message);
    } finally {
      window.dispatchEvent(new CustomEvent("appointments:refetch"));
    }
  };

  const handleMobileDateChange = (date) => setCurrentDate(date);

  /* ---------- Render ---------- */
  return (
    <div className="min-h-screen bg-background">
      <Header isCollapsed={sidebarCollapsed} onToggleSidebar={handleToggleSidebar} />
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={handleToggleSidebar} />

      <main
        className={`page-top transition-all duration-300 ease-out ${sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
          } pb-20 lg:pb-0`}
      >
        {/* Desktop */}
        <div className="hidden lg:block">
          <div className="p-6">
            <Breadcrumbs items={breadcrumbItems} className="mb-6" />

            <div className="flex gap-6">
              <div className="flex-1">
                {/* Header de calendar + control de program de lucru + interval */}
                <div className="flex items-center justify-between gap-3">
                  <CalendarHeader
                    currentDate={currentDate}
                    view={calendarView}
                    onViewChange={handleViewChange}
                    onNavigate={handleNavigate}
                    onTodayClick={handleTodayClick}
                    onCreateAppointment={() => setShowAppointmentSidebar(true)}
                  />
                  <div className="flex items-center gap-4">
                    <div className="hidden md:flex items-center gap-1 text-xs text-muted-foreground">
                      <span>Interval:</span>
                      <select
                        value={slotMinutes}
                        onChange={handleSlotChange}
                        className="rounded border px-2 py-1 text-xs bg-background"
                      >
                        <option value={5}>5 min</option>
                        <option value={10}>10 min</option>
                        <option value={15}>15 min</option>
                        <option value={20}>20 min</option>
                        <option value={30}>30 min</option>
                        <option value={60}>60 min</option>
                      </select>
                    </div>
                    {/*
                      Programul de lucru e întotdeauna pe o locație specifică.
                      Dacă owner-ul a selectat o sucursală în "scope" din Header,
                      o folosim direct aici ca să nu depindem de current_location_id.
                    */}
                    <WorkingHoursControl
                      locationId={
                        isOwnerLike && locationScopeId
                          ? Number(locationScopeId) || null
                          : null
                      }
                    />
                  </div>
                </div>

                {selectedStaff && selectedStaff !== "all" && (() => {
                  const found = (staffOptions || []).find((s) => String(s.id) === String(selectedStaff));
                  const name = found?.label || `#${selectedStaff}`;
                  return (
                    <div className="mt-3 flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 border border-primary/30 px-3 py-1 text-xs font-medium text-primary">
                        <span>Personal filtrat:</span>
                        <span className="font-semibold">{name}</span>
                        <button
                          type="button"
                          onClick={() => setSelectedStaff("all")}
                          className="ml-1 rounded-full hover:bg-primary/20 p-0.5"
                          title="Șterge filtrul"
                        >
                          ✕
                        </button>
                      </span>
                    </div>
                  );
                })()}

                <div className="mt-6">
                  <CalendarView
                    view={calendarView}
                    currentDate={currentDate}
                    onAppointmentClick={handleAppointmentClick}
                    onDateSelect={handleDateSelect}
                    selectedStaff={selectedStaff}
                    appointments={appointments}
                    workingHours={workingHours}
                    slotMinutes={slotMinutes}
                    timeOffPeriods={timeOffPeriods}
                  />
                </div>
              </div>

              <AppointmentSidebar
                isOpen={showAppointmentSidebar}
                onClose={() => setShowAppointmentSidebar(false)}
                selectedStaff={selectedStaff}
                onStaffChange={handleStaffChange}
                onCreateAppointment={handleCreateAppointment}
              />
            </div>
          </div>
        </div>

        {/* Mobile */}
        <div className="block lg:hidden px-2 pt-4 pb-24">
          <Breadcrumbs items={breadcrumbItems} className="mb-4" />
          <MobileCalendarView
            currentDate={currentDate}
            view={calendarView}
            onViewChange={handleViewChange}
            onAppointmentClick={handleAppointmentClick}
            onDateChange={handleMobileDateChange}
            onDateSelect={handleDateSelect}
            onOpenDatePicker={openDatePicker}
            selectedStaff={selectedStaff}
            staffOptions={staffOptions}
            selectedStaffIds={mobileStaffIds}
            onSelectedStaffIdsChange={setMobileStaffIds}
            appointments={appointments}
            onCreateAppointment={handleCreateAppointment}
            workingHours={workingHours}
            slotMinutes={slotMinutes}
          />
        </div>

        {showDatePicker && (
          <div
            className="fixed inset-0 z-[300] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            onMouseDown={() => setShowDatePicker(false)}
            onTouchStart={() => setShowDatePicker(false)}
          >
            <div
              className="w-full max-w-sm bg-card border border-border rounded-2xl p-4"
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >

              {/* Header: luna + săgeți */}
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-foreground capitalize">
                  {monthLabel(dpMonth)}
                </h3>

                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className="p-2 rounded-md hover:bg-muted"
                    onClick={() =>
                      setDpMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))
                    }
                    title="Luna anterioară"
                  >
                    <Icon name="ChevronLeft" size={18} />
                  </button>

                  <button
                    type="button"
                    className="p-2 rounded-md hover:bg-muted"
                    onClick={() =>
                      setDpMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))
                    }
                    title="Luna următoare"
                  >
                    <Icon name="ChevronRight" size={18} />
                  </button>

                  <button
                    type="button"
                    className="p-2 rounded-md hover:bg-muted"
                    onClick={() => setShowDatePicker(false)}
                    title="Închide"
                  >
                    <Icon name="X" size={18} />
                  </button>
                </div>
              </div>

              {/* Zilele săptămânii */}
              <div className="grid grid-cols-7 text-xs text-text-secondary mb-2">
                {weekDays.map((wd) => (
                  <div key={wd} className="text-center py-1">
                    {wd}
                  </div>
                ))}
              </div>

              {/* Grid zile */}
              <div className="grid grid-cols-7 gap-1">
                {buildMonthGrid(dpMonth).map((cell) => {
                  const today = new Date();
                  const selected = isSameDay(cell.date, dpSelected);
                  const isTodayCell = isSameDay(cell.date, today);

                  return (
                    <button
                      key={`${cell.date.getFullYear()}-${cell.date.getMonth()}-${cell.date.getDate()}`}
                      type="button"
                      onClick={() => setDpSelected(cell.date)}
                      className={[
                        "h-10 w-full rounded-lg flex items-center justify-center text-sm transition",
                        cell.inMonth ? "text-foreground" : "text-muted-foreground opacity-60",
                        selected ? "bg-primary text-primary-foreground" : "hover:bg-muted",
                        !selected && isTodayCell ? "ring-1 ring-primary/40" : "",
                      ].join(" ")}
                    >
                      {cell.date.getDate()}
                    </button>
                  );
                })}
              </div>

              {/* Footer acțiuni */}
              <div className="mt-4 flex items-center justify-between">
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => {
                    const t = new Date();
                    setDpSelected(t);
                    setDpMonth(new Date(t.getFullYear(), t.getMonth(), 1));
                  }}
                >
                  Astăzi
                </Button>

                <div className="flex gap-2">
                  <Button variant="ghost" type="button" onClick={() => setShowDatePicker(false)}>
                    Anulează
                  </Button>
                  <Button variant="default" type="button" onClick={applyDatePicker}>
                    Mergi
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <AppointmentModal
          isOpen={showAppointmentModal}
          onClose={() => setShowAppointmentModal(false)}
          appointment={selectedAppointment}
          onSave={handleAppointmentSave}
          onDelete={handleAppointmentDelete}
          mode={modalMode}
        />

        {/* <QuickActionButton /> */}
      </main>
    </div>
  );
};

export default AppointmentCalendar;
